/**
 * 🚫 NO PRODUCTS FOUND COMPONENT
 *
 * Displays helpful messages and smart suggestions when no products are found
 * after user-initiated filtering. Follows Embarc's UX pattern but with
 * intelligent suggestions to help users find relevant products.
 *
 * 🎯 FEATURES:
 * - Clear, friendly messaging
 * - Smart suggestions based on current filters
 * - Easy filter clearing
 * - Accessibility compliant (WCAG AAA)
 * - Matches Catalyst design system
 *
 * 🔗 RELATED FILES:
 * - ~/utils/fallback-redirects.ts - Smart suggestion generation
 * - ~/components/universal-product-grid.tsx - Where this component is used
 * - ~/components/universal-filter-menu.tsx - Filter clearing functionality
 */

import React from 'react';
import { Link } from '@remix-run/react';
import { generateSmartSuggestions, type SmartSuggestion } from '~/utils/fallback-redirects';
import type { FilterFacets } from '~/api/brand-filtering.server';

interface NoProductsFoundProps {
  pageType: 'deals' | 'shop' | 'category' | 'brand' | 'homepage';
  filters: Record<string, string>;
  baseFacets?: FilterFacets;
  onClearFilters?: () => void;
  categorySlug?: string;
  brandSlug?: string;
}

export default function NoProductsFound({
  pageType,
  filters,
  baseFacets,
  onClearFilters,
  categorySlug,
  brandSlug,
}: NoProductsFoundProps) {
  // Generate smart suggestions based on current context
  // Safe URL handling for SSR - fallback to shop page if window is not available
  const fallbackUrl =
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:5173/shop'
      : 'https://catalyst-cannabis.com/shop';

  const currentUrl =
    typeof window !== 'undefined' ? new URL(window.location.href) : new URL(fallbackUrl);

  const suggestions = generateSmartSuggestions({
    pageType,
    filters,
    baseFacets,
    url: currentUrl,
    categorySlug,
    brandSlug,
  });

  // Get applied filter names for display
  const appliedFilters = Object.entries(filters)
    .filter(([_, value]) => value.trim() !== '')
    .map(([key, value]) => {
      // Format filter names nicely
      const formattedKey = key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      return `${formattedKey}: ${value}`;
    });

  return (
    <div
      className="flex flex-col items-center justify-center px-4 py-16"
      role="status"
      aria-live="polite"
    >
      {/* Icon and Main Message */}
      <div className="mb-6 text-center">
        <div className="mb-4 text-6xl" aria-hidden="true">
          🔍
        </div>
        <h3 className="mb-2 text-2xl font-semibold text-gray-800">No Products Found</h3>
        <p className="max-w-md text-lg text-gray-600">
          {appliedFilters.length > 0
            ? `No products match your current filters`
            : `No products available right now`}
        </p>
      </div>

      {/* Applied Filters Display */}
      {appliedFilters.length > 0 && (
        <div className="mb-8 text-center">
          <p className="mb-3 text-sm text-gray-500">Current filters:</p>
          <div className="mb-4 flex flex-wrap justify-center gap-2">
            {appliedFilters.map((filter, index) => (
              <span
                key={index}
                className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700"
              >
                {filter}
              </span>
            ))}
          </div>

          {/* Clear Filters Button */}
          {onClearFilters && (
            <button
              onClick={onClearFilters}
              className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
            >
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              Clear All Filters
            </button>
          )}
        </div>
      )}

      {/* Smart Suggestions */}
      {suggestions.length > 0 && (
        <div className="max-w-2xl text-center">
          <p className="mb-6 text-gray-600">
            Try these suggestions to find what you're looking for:
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {suggestions.map((suggestion, index) => (
              <SuggestionCard key={index} suggestion={suggestion} />
            ))}
          </div>
        </div>
      )}

      {/* Screen Reader Additional Info */}
      <div className="sr-only">
        Try adjusting your filters, clearing all filters, or browse our suggested categories to find
        products.
      </div>
    </div>
  );
}

/**
 * Individual suggestion card component
 */
function SuggestionCard({ suggestion }: { suggestion: SmartSuggestion }) {
  // Get appropriate icon and styling based on suggestion type
  const getTypeConfig = (type: SmartSuggestion['type']) => {
    switch (type) {
      case 'brand':
        return {
          icon: '🏷️',
          bgColor: 'bg-blue-50 hover:bg-blue-100',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-200',
        };
      case 'category':
        return {
          icon: '📂',
          bgColor: 'bg-green-50 hover:bg-green-100',
          textColor: 'text-green-700',
          borderColor: 'border-green-200',
        };
      case 'deals':
        return {
          icon: '🎯',
          bgColor: 'bg-red-50 hover:bg-red-100',
          textColor: 'text-red-700',
          borderColor: 'border-red-200',
        };
      case 'shop':
        return {
          icon: '🛍️',
          bgColor: 'bg-purple-50 hover:bg-purple-100',
          textColor: 'text-purple-700',
          borderColor: 'border-purple-200',
        };
      default:
        return {
          icon: '✨',
          bgColor: 'bg-gray-50 hover:bg-gray-100',
          textColor: 'text-gray-700',
          borderColor: 'border-gray-200',
        };
    }
  };

  const config = getTypeConfig(suggestion.type);

  return (
    <Link
      to={suggestion.url}
      className={`flex items-center justify-center rounded-lg border-2 p-4 transition-all duration-200 ${config.bgColor} ${config.textColor} ${config.borderColor} min-h-[44px] text-center hover:shadow-md focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none`}
    >
      <span className="mr-3 text-lg" aria-hidden="true">
        {config.icon}
      </span>
      <span className="font-medium">{suggestion.label}</span>
    </Link>
  );
}
